"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  PhoneIcon,
  MailIcon,
  MapPinIcon,
  ClockIcon,
  SendIcon,
  CheckCircleIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { JoinSection } from "@/components/join-section";
import type { LocaleCode, SimpleSectionContent } from "@/types/sections";
import type { ContactPageContent } from "@/types/pages";
import { pickLocalizedString } from "@/lib/locale";
import { resolveIcon } from "@/lib/icon-map";

import { db } from "@/lib/firebase.client";
import { doc, onSnapshot } from "firebase/firestore";

interface ContactForm {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

type FSSettings = {
  contact?: {
    address?: string;
    phone?: string;
    phoneSecondary?: string;
    email?: string;
    hours?: { en?: string; fr?: string };
    googleMapIframe?: string;
  };
  confidentialCode?: string;
};

function mirrorLocale<T>(val: { en?: T; fr?: T } = {}): { en?: T; fr?: T } {
  const a = val?.en;
  const b = val?.fr;
  if (!a && b) return { en: b, fr: b };
  if (!b && a) return { en: a, fr: a };
  return { en: a, fr: b };
}

export default function ContactPageClient({
  locale,
  content,
  joinSection,
}: {
  locale: LocaleCode;
  content: ContactPageContent;
  joinSection: SimpleSectionContent;
}) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [settings, setSettings] = useState<FSSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactForm>();

  // Subscribe to website settings to populate address & map
  useEffect(() => {
    const docId = process.env.NEXT_PUBLIC_WEBSITE_SETTINGS_DOC_ID || "default";
    const ref = doc(db, "website-settings", docId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as FSSettings | undefined;
        const mirroredHours = mirrorLocale(data?.contact?.hours ?? {});
        setSettings({
          contact: {
            ...data?.contact,
            hours: mirroredHours,
          },
        });
        setLoadingSettings(false);
      },
      () => setLoadingSettings(false)
    );
    return () => unsub();
  }, []);

  const onSubmit = async (data: ContactForm) => {
    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: "contact@uncommon12.ch",
          subject: `Contact form: ${data.subject}`,
          bodyTemplate: "contact",
          layoutTemplate: "contact",
          templateData: {
            name: data.name,
            email: data.email,
            phone: data.phone ?? "",
            subject: data.subject,
            message: data.message,
          },
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const rawError = payload?.error;
        const errorMessage =
          typeof rawError === "string"
            ? rawError
            : rawError?.message ??
              rawError?.response ??
              rawError?.code ??
              `Send failed (${response.status})`;
        throw new Error(errorMessage);
      }

      setIsSubmitted(true);
      reset();
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
        console.error("Contact form send error", error);
        alert(
          error instanceof Error
            ? `Send failed: ${error.message}`
            : "Send failed"
        );
    }
  };

  const address =
    settings?.contact?.address ??
    `Base Geneva
Rue du Jura 13
1201 Genève, Suisse`;

  const phone = settings?.contact?.phone ?? "+41-79-918-18-78";
  const secondaryPhone = settings?.contact?.phoneSecondary?.trim();
  const email = settings?.contact?.email ?? "contact@uncommon12.ch";
  const hours =
    (locale === "fr"
      ? settings?.contact?.hours?.fr
      : settings?.contact?.hours?.en) ??
    (locale === "fr"
      ? "Lundi - Vendredi: 7h00 - 23h00\nWeek-end: 10h00 - 19h00"
      : "Monday - Friday: 7AM - 11PM\nWeekend: 10AM - 7PM");

  const mapSrc =
    settings?.contact?.googleMapIframe ??
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3429.758317194271!2d6.134792776314882!3d46.20919577109592!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c65091a4757b3%3A0x41c8d91a374afbe2!2sBase%20Geneva!5e1!3m2!1sen!2sma!4v1752614409623!5m2!1sen!2sma";

  const heroTitle = pickLocalizedString(content.hero.title, locale, "Contactez-nous");
  const heroSubtitle = pickLocalizedString(content.hero.subtitle, locale);
  const heroDescription = pickLocalizedString(content.hero.description, locale);
  const heroEyebrow = pickLocalizedString(content.hero.eyebrow, locale);
  const HeroIcon = resolveIcon(content.hero.iconKey);

  const infoHeading = pickLocalizedString(
    content.info.heading,
    locale,
    locale === "fr" ? "Informations de contact" : "Contact information"
  );
  const addressLabel = pickLocalizedString(content.info.addressLabel, locale, "Adresse");
  const phoneLabel = pickLocalizedString(content.info.phoneLabel, locale, "Téléphone");
  const secondaryPhoneLabel =
    locale === "fr" ? "Téléphone secondaire" : "Secondary phone";
  const emailLabel = pickLocalizedString(content.info.emailLabel, locale, "Email");
  const hoursLabel = pickLocalizedString(content.info.hoursLabel, locale, "Horaires");

  const formTitle = pickLocalizedString(content.form.title, locale, "Envoyez-nous un message");
  const formDescription = pickLocalizedString(content.form.description, locale);
  const successTitle = pickLocalizedString(content.form.successTitle, locale);
  const successMessage = pickLocalizedString(content.form.successMessage, locale);
  const submitLabel = pickLocalizedString(content.form.submitLabel, locale, "Envoyer le message");
  const submittingLabel = pickLocalizedString(
    content.form.submittingLabel,
    locale,
    "Envoi en cours..."
  );

  const mapTitle = pickLocalizedString(content.map.title, locale, "Notre emplacement");
  const homeLabel = locale === "fr" ? "Accueil" : "Home";

  return (
    <div className="min-h-screen font-[family-name:var(--font-geist-sans)]">
      <main className="w-full">
        <div className="relative h-[40vh] bg-gradient-to-br from-[#FCD2C3] to-[#BBD8E6] overflow-hidden">
          <div className="absolute inset-0 bg-black/10" />
          <div className="relative h-full flex items-center justify-center px-4">
            <div className="text-center max-w-3xl">
              <Breadcrumb className="mb-6 justify-center">
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link href={`/${locale}`}>{homeLabel}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{heroTitle}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <div className="flex justify-center mb-4">
                <div className="w-fit rounded-lg border border-black/20 p-2 bg-white/70 dark:bg-black/50">
                  <HeroIcon className="h-5 w-5 text-black dark:text-white" />
                </div>
              </div>
              {heroEyebrow && (
                <p className="text-xs uppercase tracking-[0.3em] text-black/60 dark:text-white/80 mb-3">
                  {heroEyebrow}
                </p>
              )}
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-4">{heroTitle}</h1>
              <p className="text-xl text-black/80 max-w-2xl mx-auto">{heroSubtitle}</p>
              {heroDescription && (
                <p className="text-base text-black/70 max-w-2xl mx-auto mt-4">{heroDescription}</p>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-black dark:text-white mb-6">
                  {infoHeading}
                </h2>
                <div className="space-y-6">
                  <InfoCard
                    label={addressLabel}
                    loading={loadingSettings}
                    icon={<MapPinIcon className="w-5 h-5 text-black" />}
                    background="bg-[#FCD2C3]"
                  >
                    <p className="text-neutral-600 dark:text-neutral-400 whitespace-pre-line">
                      {loadingSettings ? "—" : address}
                    </p>
                  </InfoCard>
                  <InfoCard
                    label={phoneLabel}
                    loading={loadingSettings}
                    icon={<PhoneIcon className="w-5 h-5 text-black" />}
                    background="bg-[#BBD8E6]"
                  >
                    <p className="text-neutral-600 dark:text-neutral-400">
                      {loadingSettings ? "—" : phone}
                    </p>
                  </InfoCard>
                  {secondaryPhone && (
                    <InfoCard
                      label={secondaryPhoneLabel}
                      loading={loadingSettings}
                      icon={<PhoneIcon className="w-5 h-5 text-black" />}
                      background="bg-[#D8D1C2]"
                    >
                      <p className="text-neutral-600 dark:text-neutral-400">
                        {loadingSettings ? "—" : secondaryPhone}
                      </p>
                    </InfoCard>
                  )}
                  <InfoCard
                    label={emailLabel}
                    loading={loadingSettings}
                    icon={<MailIcon className="w-5 h-5 text-black" />}
                    background="bg-[#AED8C7]"
                  >
                    <p className="text-neutral-600 dark:text-neutral-400">
                      {loadingSettings ? "—" : email}
                    </p>
                  </InfoCard>
                  <InfoCard
                    label={hoursLabel}
                    loading={loadingSettings}
                    icon={<ClockIcon className="w-5 h-5 text-black" />}
                    background="bg-[#F2D5A3]"
                  >
                    <p className="text-neutral-600 dark:text-neutral-400 whitespace-pre-line">
                      {loadingSettings ? "—" : hours}
                    </p>
                  </InfoCard>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl">{formTitle}</CardTitle>
                  <p className="text-neutral-600 dark:text-neutral-400">{formDescription}</p>
                </CardHeader>
                <CardContent>
                  {isSubmitted && (
                    <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                      <div>
                        {successTitle && (
                          <p className="font-semibold text-green-800 dark:text-green-200">
                            {successTitle}
                          </p>
                        )}
                        <p className="text-green-800 dark:text-green-200">{successMessage}</p>
                      </div>
                    </div>
                  )}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Field
                        label={locale === "fr" ? "Nom complet *" : "Full name *"}
                        error={errors.name?.message}
                      >
                        <Input
                          {...register("name", { required: locale === "fr" ? "Le nom est requis" : "Name is required" })}
                          placeholder={locale === "fr" ? "Votre nom complet" : "Your full name"}
                          className={errors.name ? "border-red-500" : ""}
                        />
                      </Field>
                      <Field label="Email *" error={errors.email?.message}>
                        <Input
                          type="email"
                          {...register("email", {
                            required: locale === "fr" ? "L'email est requis" : "Email is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: locale === "fr" ? "Email invalide" : "Invalid email",
                            },
                          })}
                          placeholder="votre@email.com"
                          className={errors.email ? "border-red-500" : ""}
                        />
                      </Field>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Field label="Téléphone / Phone">
                        <Input type="tel" {...register("phone")} placeholder="+41 XX XXX XX XX" />
                      </Field>
                      <Field label={locale === "fr" ? "Sujet *" : "Subject *"} error={errors.subject?.message}>
                        <Input
                          {...register("subject", {
                            required: locale === "fr" ? "Le sujet est requis" : "Subject is required",
                          })}
                          placeholder={locale === "fr" ? "Sujet de votre message" : "Message subject"}
                          className={errors.subject ? "border-red-500" : ""}
                        />
                      </Field>
                    </div>

                    <Field label={locale === "fr" ? "Message *" : "Message *"} error={errors.message?.message}>
                      <Textarea
                        {...register("message", {
                          required: locale === "fr" ? "Le message est requis" : "Message is required",
                        })}
                        placeholder={locale === "fr" ? "Votre message..." : "Your message..."}
                        rows={6}
                        className={errors.message ? "border-red-500" : ""}
                      />
                    </Field>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#FCD2C3] text-black hover:bg-[#FCD2C3]/90 flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                          {submittingLabel}
                        </>
                      ) : (
                        <>
                          <SendIcon className="w-4 h-4" />
                          {submitLabel}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="mt-16">
            <h2 className="text-2xl font-bold text-black dark:text-white mb-8 text-center">
              {mapTitle}
            </h2>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src={mapSrc}
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Uncommon12 location"
              />
            </div>
          </div>
          <JoinSection content={joinSection} locale={locale} />
        </div>
      </main>
    </div>
  );
}

function InfoCard({
  label,
  icon,
  background,
  loading,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  background: string;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className={`w-12 h-12 ${background} rounded-full flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-black dark:text-white mb-1">
          {loading ? "…" : label}
        </h3>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-black dark:text-white mb-2">{label}</label>
      {children}
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
